import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { scenarioAPI, regionAPI } from '../../lib/api';
import { defaultTreeSpecies, defaultRiskScenario, formatCurrency, formatNumber, chartColors } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { 
  Calculator as CalcIcon, 
  TreePine, 
  Save, 
  Download, 
  RefreshCw,
  AlertTriangle,
  Info,
  Loader2,
  TrendingUp,
  BadgeEuro,
  Leaf,
  Sparkles,
  BookmarkPlus
} from 'lucide-react';

const AvanceradKalkylator = () => {
  const { t, language } = useLanguage();
  
  // Form state
  const [name, setName] = useState('');
  const [region, setRegion] = useState('Svealand');
  const [area, setArea] = useState(10);
  const [period, setPeriod] = useState(30);
  const [species, setSpecies] = useState('gran');
  const [treeData, setTreeData] = useState(defaultTreeSpecies.gran);
  const [enableRisk, setEnableRisk] = useState(false);
  const [riskData, setRiskData] = useState(defaultRiskScenario);
  const [discountRate, setDiscountRate] = useState(3);
  
  // Results state
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Regional defaults
  const [regionalDefaults, setRegionalDefaults] = useState({});

  const content = {
    sv: {
      title: 'Avancerad skogskalkyl',
      subtitle: 'Komplett ekonomisk analys av skogsbruk med riskscenarier, tillväxtmodeller och långsiktiga prognoser.',
      scenarioName: 'Scenarionamn',
      region: 'Region',
      treeSpecies: 'Trädslag',
      area: 'Areal (ha)',
      period: 'Period (år)',
      preview: 'Förhandsvisa',
      calculate: 'Beräkna',
      saveCalculation: 'Spara beräkning',
      savingComingSoon: 'Sparfunktionen kommer snart.',
      loadDefaults: 'Ladda regionala standardvärden',
      growthManaged: 'Tillväxt skött (m³/ha/år)',
      growthNeglected: 'Tillväxt oskött (m³/ha/år)',
      priceTimber: 'Sågtimmer (SEK/m³)',
      pricePulpwood: 'Massaved (SEK/m³)',
      clearingCost: 'Röjning (SEK/ha)',
      plantingCost: 'Plantering (SEK/ha)',
      thinningCost: 'Gallring (SEK/ha)',
      discountRate: 'Diskonteringsränta (%)',
      riskTitle: 'Riskscenarier',
      results: 'Resultat',
      totalBenefit: 'Total nettonytta',
      breakeven: 'Brytpunkt',
      noBreakeven: 'Ingen brytpunkt',
      year: 'år',
      years: 'år',
      cumulativeBenefit: 'Kumulativ nytta',
      volume: 'Volym',
      netBenefit: 'Nettoförändring',
      managedForest: 'Skött skog',
      neglectedForest: 'Oskött skog',
      extraVolume: 'Extra volym',
      extraRevenue: 'Extra intäkt',
      cost: 'Kostnad',
      noResults: 'Inga resultat ännu',
      noResultsText: 'Fyll i parametrarna och klicka på "Förhandsvisa" för att se resultaten.',
      regions: { norrland: 'Norrland', svealand: 'Svealand', gotaland: 'Götaland' },
      species: { gran: 'Gran', tall: 'Tall', bjork: 'Björk' },
      stormProb: 'Storm (%)',
      stormDamage: 'Stormskada (%)',
      beetleProb: 'Barkborre (%)',
      beetleDamage: 'Barkborreskada (%)',
      fireProb: 'Brand (%)',
      fireDamage: 'Brandskada (%)',
      advancedNote: 'Detta är den mest avancerade kalkylatorn med fullständig ekonomisk modellering.'
    },
    de: {
      title: 'Erweiterter Waldrechner',
      subtitle: 'Vollständige wirtschaftliche Analyse der Forstwirtschaft mit Risikoszenarien und langfristigen Prognosen.',
      scenarioName: 'Szenarioname',
      region: 'Region',
      treeSpecies: 'Baumart',
      area: 'Fläche (ha)',
      period: 'Zeitraum (Jahre)',
      preview: 'Vorschau',
      calculate: 'Berechnen',
      saveCalculation: 'Berechnung speichern',
      savingComingSoon: 'Speicherfunktion kommt bald.',
      loadDefaults: 'Regionale Standardwerte laden',
      growthManaged: 'Zuwachs bewirtschaftet (m³/ha/Jahr)',
      growthNeglected: 'Zuwachs unbewirtschaftet (m³/ha/Jahr)',
      priceTimber: 'Sägeholz (SEK/m³)',
      pricePulpwood: 'Zellstoff (SEK/m³)',
      clearingCost: 'Läuterung (SEK/ha)',
      plantingCost: 'Pflanzung (SEK/ha)',
      thinningCost: 'Durchforstung (SEK/ha)',
      discountRate: 'Diskontierungssatz (%)',
      riskTitle: 'Risikoszenarien',
      results: 'Ergebnisse',
      totalBenefit: 'Gesamtnettonutzen',
      breakeven: 'Break-Even',
      noBreakeven: 'Kein Break-Even',
      year: 'Jahr',
      years: 'Jahre',
      cumulativeBenefit: 'Kumulativer Nutzen',
      volume: 'Volumen',
      netBenefit: 'Nettoveränderung',
      managedForest: 'Bewirtschafteter Wald',
      neglectedForest: 'Unbewirtschafteter Wald',
      extraVolume: 'Zusätzliches Volumen',
      extraRevenue: 'Zusätzlicher Ertrag',
      cost: 'Kosten',
      noResults: 'Noch keine Ergebnisse',
      noResultsText: 'Füllen Sie die Parameter aus und klicken Sie auf "Vorschau".',
      regions: { norrland: 'Nordschweden', svealand: 'Mittelschweden', gotaland: 'Südschweden' },
      species: { gran: 'Fichte', tall: 'Kiefer', bjork: 'Birke' },
      stormProb: 'Sturm (%)',
      stormDamage: 'Sturmschaden (%)',
      beetleProb: 'Borkenkäfer (%)',
      beetleDamage: 'Borkenkäferschaden (%)',
      fireProb: 'Brand (%)',
      fireDamage: 'Brandschaden (%)',
      advancedNote: 'Dies ist der fortschrittlichste Rechner mit vollständiger wirtschaftlicher Modellierung.'
    },
    en: {
      title: 'Advanced Forest Calculator',
      subtitle: 'Complete economic analysis of forestry with risk scenarios and long-term projections.',
      scenarioName: 'Scenario name',
      region: 'Region',
      treeSpecies: 'Tree species',
      area: 'Area (ha)',
      period: 'Period (years)',
      preview: 'Preview',
      calculate: 'Calculate',
      saveCalculation: 'Save calculation',
      savingComingSoon: 'Save feature coming soon.',
      loadDefaults: 'Load regional defaults',
      growthManaged: 'Growth managed (m³/ha/year)',
      growthNeglected: 'Growth neglected (m³/ha/year)',
      priceTimber: 'Sawlog (SEK/m³)',
      pricePulpwood: 'Pulpwood (SEK/m³)',
      clearingCost: 'Clearing (SEK/ha)',
      plantingCost: 'Planting (SEK/ha)',
      thinningCost: 'Thinning (SEK/ha)',
      discountRate: 'Discount rate (%)',
      riskTitle: 'Risk scenarios',
      results: 'Results',
      totalBenefit: 'Total net benefit',
      breakeven: 'Break-even',
      noBreakeven: 'No break-even',
      year: 'year',
      years: 'years',
      cumulativeBenefit: 'Cumulative benefit',
      volume: 'Volume',
      netBenefit: 'Net change',
      managedForest: 'Managed forest',
      neglectedForest: 'Neglected forest',
      extraVolume: 'Extra volume',
      extraRevenue: 'Extra revenue',
      cost: 'Cost',
      noResults: 'No results yet',
      noResultsText: 'Fill in the parameters and click "Preview" to see the results.',
      regions: { norrland: 'Northern Sweden', svealand: 'Central Sweden', gotaland: 'Southern Sweden' },
      species: { gran: 'Spruce', tall: 'Pine', bjork: 'Birch' },
      stormProb: 'Storm (%)',
      stormDamage: 'Storm damage (%)',
      beetleProb: 'Bark beetle (%)',
      beetleDamage: 'Bark beetle damage (%)',
      fireProb: 'Fire (%)',
      fireDamage: 'Fire damage (%)',
      advancedNote: 'This is the most advanced calculator with complete economic modeling.'
    },
    se: {
      title: 'Ovddiduvvon meahccerehkenastin',
      subtitle: 'Ollislaš ekonomalaš analysa meahccedoalus riskaskenariojiin.',
      scenarioName: 'Skenárionamma',
      region: 'Guovlu',
      treeSpecies: 'Muorralágat',
      area: 'Areála (ha)',
      period: 'Áigodat (jahkát)',
      preview: 'Ovdačájáhus',
      calculate: 'Rehkenas',
      saveCalculation: 'Vurke rehkenastima',
      savingComingSoon: 'Vurkedoaibma boahtá fargga.',
      loadDefaults: 'Viečča guovllu standárdaárvvuid',
      noResults: 'Eai leat bohtosat vel',
      noResultsText: 'Deavdde paramehterežid ja coahkkil "Ovdačájáhus".',
      advancedNote: 'Dát lea ovddidumos rehkenastin ollislaš ekonomalaš modelleremiin.'
    }
  };

  const c = content[language] || content.sv;

  useEffect(() => {
    loadRegionalDefaults();
  }, []);

  useEffect(() => {
    setTreeData(defaultTreeSpecies[species] || defaultTreeSpecies.gran);
  }, [species]);

  const loadRegionalDefaults = async () => {
    try {
      const response = await regionAPI.getAll();
      setRegionalDefaults(response.data.defaults);
    } catch (error) {
      console.error('Failed to load regional defaults:', error);
    }
  };

  const applyRegionalDefaults = () => {
    const regionData = regionalDefaults[region];
    if (regionData && regionData[species]) {
      const defaults = regionData[species];
      setTreeData(prev => ({
        ...prev,
        growth_managed: defaults.growth_managed || prev.growth_managed,
        growth_neglected: defaults.growth_neglected || prev.growth_neglected,
        price_timber: defaults.price_timber || prev.price_timber
      }));
      toast.success(c.loadDefaults);
    }
  };

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const payload = {
        name: name || 'Nytt scenario',
        region,
        area_hectares: area,
        period_years: period,
        tree_species: {
          ...treeData,
          species
        },
        risk_scenario: enableRisk ? riskData : null,
        discount_rate: discountRate / 100,
        language
      };

      const response = await scenarioAPI.preview(payload);
      setResults(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || 'Calculation failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCalculation = () => {
    toast.info(c.savingComingSoon);
  };

  const updateTreeData = (field, value) => {
    setTreeData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const updateRiskData = (field, value) => {
    setRiskData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const chartData = results?.results?.map((r) => ({
    year: r.year,
    managed: r.managed_volume,
    neglected: r.neglected_volume,
    benefit: r.cumulative_benefit,
    netBenefit: r.net_benefit
  })) || [];

  return (
    <Card className="w-full border-2 border-forest-300" data-testid="avancerad-calculator">
      <CardHeader className="bg-gradient-to-r from-forest-50 to-emerald-50">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-forest-700">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl text-forest-800">{c.title}</CardTitle>
            <CardDescription>{c.subtitle}</CardDescription>
          </div>
        </div>
        <Badge variant="outline" className="w-fit mt-2 border-forest-400 text-forest-700">
          <Sparkles className="h-3 w-3 mr-1" />
          {c.advancedNote}
        </Badge>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Panel */}
          <div className="lg:col-span-1 space-y-4">
            {/* Basic Info */}
            <div className="space-y-4 p-4 bg-forest-50 rounded-lg">
              <h3 className="font-semibold text-forest-800 flex items-center gap-2">
                <TreePine className="h-4 w-4" />
                {c.scenarioName}
              </h3>
              
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mitt skogsscenario"
                data-testid="scenario-name"
              />

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{c.region}</Label>
                  <Select value={region} onValueChange={setRegion}>
                    <SelectTrigger data-testid="region-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Norrland">{c.regions?.norrland || 'Norrland'}</SelectItem>
                      <SelectItem value="Svealand">{c.regions?.svealand || 'Svealand'}</SelectItem>
                      <SelectItem value="Götaland">{c.regions?.gotaland || 'Götaland'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{c.treeSpecies}</Label>
                  <Select value={species} onValueChange={setSpecies}>
                    <SelectTrigger data-testid="species-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gran">{c.species?.gran || 'Gran'}</SelectItem>
                      <SelectItem value="tall">{c.species?.tall || 'Tall'}</SelectItem>
                      <SelectItem value="bjork">{c.species?.bjork || 'Björk'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{c.area}</Label>
                  <Input
                    type="number"
                    value={area}
                    onChange={(e) => setArea(parseFloat(e.target.value) || 0)}
                    min="1"
                    data-testid="area-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{c.period}</Label>
                  <Input
                    type="number"
                    value={period}
                    onChange={(e) => setPeriod(parseInt(e.target.value) || 10)}
                    min="5"
                    max="100"
                    data-testid="period-input"
                  />
                </div>
              </div>

              <Button 
                variant="outline" 
                size="sm" 
                onClick={applyRegionalDefaults}
                className="w-full"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {c.loadDefaults}
              </Button>
            </div>

            {/* Growth & Prices */}
            <div className="space-y-3 p-4 bg-white border rounded-lg">
              <h4 className="text-sm font-medium text-forest-700">
                {language === 'sv' ? 'Tillväxt & Priser' : 'Growth & Prices'}
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">{c.growthManaged}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={treeData.growth_managed}
                    onChange={(e) => updateTreeData('growth_managed', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{c.growthNeglected}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={treeData.growth_neglected}
                    onChange={(e) => updateTreeData('growth_neglected', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">{c.priceTimber}</Label>
                  <Input
                    type="number"
                    value={treeData.price_timber}
                    onChange={(e) => updateTreeData('price_timber', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{c.pricePulpwood}</Label>
                  <Input
                    type="number"
                    value={treeData.price_pulpwood}
                    onChange={(e) => updateTreeData('price_pulpwood', e.target.value)}
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">{c.clearingCost}</Label>
                  <Input
                    type="number"
                    value={treeData.clearing_cost}
                    onChange={(e) => updateTreeData('clearing_cost', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{c.plantingCost}</Label>
                  <Input
                    type="number"
                    value={treeData.planting_cost}
                    onChange={(e) => updateTreeData('planting_cost', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{c.thinningCost}</Label>
                  <Input
                    type="number"
                    value={treeData.thinning_cost}
                    onChange={(e) => updateTreeData('thinning_cost', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">{c.discountRate}</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={discountRate}
                  onChange={(e) => setDiscountRate(parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>

            {/* Risk Scenarios */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {c.riskTitle}
                </h4>
                <Switch 
                  checked={enableRisk} 
                  onCheckedChange={setEnableRisk}
                />
              </div>
              {enableRisk && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="space-y-1">
                    <Label className="text-xs">{c.stormProb}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={riskData.storm_probability * 100}
                      onChange={(e) => updateRiskData('storm_probability', parseFloat(e.target.value) / 100)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{c.stormDamage}</Label>
                    <Input
                      type="number"
                      step="1"
                      value={riskData.storm_damage * 100}
                      onChange={(e) => updateRiskData('storm_damage', parseFloat(e.target.value) / 100)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{c.beetleProb}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={riskData.bark_beetle_probability * 100}
                      onChange={(e) => updateRiskData('bark_beetle_probability', parseFloat(e.target.value) / 100)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{c.beetleDamage}</Label>
                    <Input
                      type="number"
                      step="1"
                      value={riskData.bark_beetle_damage * 100}
                      onChange={(e) => updateRiskData('bark_beetle_damage', parseFloat(e.target.value) / 100)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button 
                onClick={handleCalculate}
                disabled={loading}
                className="flex-1 bg-forest-700 hover:bg-forest-800"
                data-testid="calculate-btn"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalcIcon className="mr-2 h-4 w-4" />}
                {c.preview}
              </Button>
            </div>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2 space-y-4">
            {results ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <p className="text-xs text-muted-foreground uppercase">{c.totalBenefit}</p>
                      <p className="text-xl font-mono font-bold text-forest-700">
                        {formatCurrency(results.total_net_benefit || 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <p className="text-xs text-muted-foreground uppercase">{c.breakeven}</p>
                      <p className="text-xl font-mono font-bold text-forest-700">
                        {results.breakeven_year 
                          ? `${results.breakeven_year} ${c.year}`
                          : c.noBreakeven}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <p className="text-xs text-muted-foreground uppercase">{c.area}</p>
                      <p className="text-xl font-mono font-bold text-timber-700">{area} ha</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <p className="text-xs text-muted-foreground uppercase">{c.period}</p>
                      <p className="text-xl font-mono font-bold text-timber-700">{period} {c.years}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg">{c.results}</CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSaveCalculation}
                        data-testid="save-calculation-btn"
                      >
                        <BookmarkPlus className="mr-2 h-4 w-4" />
                        {c.saveCalculation}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="cumulative">
                      <TabsList className="mb-4">
                        <TabsTrigger value="cumulative">{c.cumulativeBenefit}</TabsTrigger>
                        <TabsTrigger value="volume">{c.volume}</TabsTrigger>
                        <TabsTrigger value="annual">{c.netBenefit}</TabsTrigger>
                      </TabsList>

                      <TabsContent value="cumulative">
                        <div className="h-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="year" />
                              <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                              <Tooltip 
                                formatter={(v) => [formatCurrency(v), c.cumulativeBenefit]}
                                contentStyle={{ borderRadius: '8px' }}
                              />
                              <Legend />
                              <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
                              {results.breakeven_year && (
                                <ReferenceLine 
                                  x={results.breakeven_year} 
                                  stroke={chartColors.growth} 
                                  strokeDasharray="5 5"
                                  label={{ value: c.breakeven, fill: chartColors.growth }}
                                />
                              )}
                              <Area 
                                type="monotone" 
                                dataKey="benefit" 
                                stroke={chartColors.profit}
                                fill={chartColors.profit}
                                fillOpacity={0.3}
                                name={c.cumulativeBenefit}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </TabsContent>

                      <TabsContent value="volume">
                        <div className="h-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="year" />
                              <YAxis />
                              <Tooltip contentStyle={{ borderRadius: '8px' }} />
                              <Legend />
                              <Line 
                                type="monotone" 
                                dataKey="managed" 
                                stroke={chartColors.profit}
                                strokeWidth={2}
                                name={c.managedForest}
                                dot={false}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="neglected" 
                                stroke={chartColors.loss}
                                strokeWidth={2}
                                name={c.neglectedForest}
                                dot={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </TabsContent>

                      <TabsContent value="annual">
                        <div className="h-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="year" />
                              <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                              <Tooltip 
                                formatter={(v) => [formatCurrency(v), c.netBenefit]}
                                contentStyle={{ borderRadius: '8px' }}
                              />
                              <ReferenceLine y={0} stroke="#6b7280" />
                              <Area 
                                type="monotone" 
                                dataKey="netBenefit" 
                                stroke={chartColors.secondary}
                                fill={chartColors.secondary}
                                fillOpacity={0.3}
                                name={c.netBenefit}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>

                {/* Results Table */}
                <Card>
                  <CardContent className="pt-4">
                    <div className="overflow-x-auto max-h-[200px]">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white">
                          <tr className="border-b">
                            <th className="py-2 px-2 text-left font-medium">{c.year}</th>
                            <th className="py-2 px-2 text-right font-medium">{c.extraVolume}</th>
                            <th className="py-2 px-2 text-right font-medium">{c.extraRevenue}</th>
                            <th className="py-2 px-2 text-right font-medium">{c.cost}</th>
                            <th className="py-2 px-2 text-right font-medium">{c.netBenefit}</th>
                            <th className="py-2 px-2 text-right font-medium">{c.cumulativeBenefit}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.results?.filter((_, i) => i % 5 === 0 || i === 0).map((row) => (
                            <tr key={row.year} className="border-b hover:bg-muted/50">
                              <td className="py-1 px-2 font-mono text-xs">{row.year}</td>
                              <td className="py-1 px-2 text-right font-mono text-xs">{formatNumber(row.extra_volume, 1)} m³</td>
                              <td className="py-1 px-2 text-right font-mono text-xs">{formatCurrency(row.extra_revenue)}</td>
                              <td className="py-1 px-2 text-right font-mono text-xs">{formatCurrency(row.management_cost)}</td>
                              <td className={`py-1 px-2 text-right font-mono text-xs ${row.net_benefit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(row.net_benefit)}
                              </td>
                              <td className={`py-1 px-2 text-right font-mono text-xs font-semibold ${row.cumulative_benefit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(row.cumulative_benefit)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="h-full">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <div className="p-4 rounded-full bg-forest-100 mb-4">
                    <Info className="h-8 w-8 text-forest-600" />
                  </div>
                  <h3 className="text-lg font-medium text-forest-800 mb-2">{c.noResults}</h3>
                  <p className="text-muted-foreground text-center max-w-md">{c.noResultsText}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AvanceradKalkylator;
